import { useState } from 'react'
import { usePhotoTagsQuery } from '@/api/hooks/use-photo-tags'
import type { PhotoStudentTag } from '@/api/types/photo'
import { MultiSelectChipCombobox } from '@/components/app/panels/right-panel/combo-box/multi-select-chip-combobox'
import { formatStudentTagLabel } from '@/lib/photos/student-tags'

export type SearchTagsComboboxProps = {
  /** Selected student ids (multi-select chips). */
  value: string[]
  onChange: (studentIds: string[]) => void
  onOpenChange?: (open: boolean) => void
}

function getStudentId(tag: PhotoStudentTag): string {
  return tag.studentId
}

function studentMatchesSearch(tag: PhotoStudentTag, query: string): boolean {
  const normalized = query.trim().toLowerCase()

  if (!normalized) return true

  return (
    tag.firstName.toLowerCase().includes(normalized) ||
    tag.lastName.toLowerCase().includes(normalized)
  )
}

export function SearchTagsComboBox({ value, onChange, onOpenChange }: SearchTagsComboboxProps) {
  const [open, setOpen] = useState(false)

  function handleOpenChange(next: boolean) {
    setOpen(next)
    onOpenChange?.(next)
  }

  const {
    data: tags = [],
    isLoading,
    isError,
    error,
  } = usePhotoTagsQuery({
    enabled: open || value.length > 0,
  })

  return (
    <MultiSelectChipCombobox
      value={value}
      onChange={onChange}
      items={tags}
      getId={getStudentId}
      getLabel={formatStudentTagLabel}
      matchesSearch={studentMatchesSearch}
      triggerLabel="Search students"
      searchPlaceholder="Search students"
      loadingMessage="Loading students…"
      errorMessage={error instanceof Error ? error.message : 'Failed to load tags'}
      chipsTestId="student-filter-chips"
      isLoading={isLoading}
      isError={isError}
      open={open}
      onOpenChange={handleOpenChange}
    />
  )
}
